import { Controller } from "@hotwired/stimulus"

// Warn, while you are still editing, when an ingredient you have removed is
// still named in the method.
//
// Remove "smoked paprika" and step 3 still says "add the smoked paprika". The
// recipe contradicts itself — the method is prose the cook follows at the
// bench, the ingredient list is what the macros come from, and the two drift
// apart silently.
//
// IN THE FORM, not after the save. A warning that arrives once you have
// already committed the change and moved to another page is a warning you
// cannot act on without going back.
//
// This controller does no matching of its own. Each row carries the words to
// look for in a data attribute, worked out in Ruby — the qualifier list, the
// comma handling, English pluralisation. Those rules took two attempts and a
// measurement across 168 real ingredients to get right, and a second copy in
// JavaScript would drift from the first. Ruby decides WHAT to look for; this
// only looks.
export default class extends Controller {
  static targets = [ "row", "notice", "list", "unused", "unusedList" ]

  connect() {
    // The editor loads its content asynchronously, so an immediate read finds
    // an empty document and reports every removal as orphaned.
    requestAnimationFrame(() => this.check())
  }

  // Removing a row, typing in the method, choosing a different food — any of
  // them can change the answer.
  //
  // Two questions, opposite directions, same comparison:
  //
  //   * an ingredient you have REMOVED that the method still names — the
  //     method now tells you to use something that is not in the list
  //   * an ingredient you are KEEPING that the method never names — the
  //     method never tells you what to do with it
  //
  // Both are the recipe disagreeing with itself. Neither blocks a save.
  check() {
    const text = this.methodText()

    if (!text) return this.render([], [])

    const orphans = []
    const unused = []

    for (const row of this.rowTargets) {
      const matched = this.namedIn(row, text)

      if (this.beingRemoved(row)) {
        if (matched) orphans.push(this.label(row, matched))
      } else if (!matched) {
        unused.push(row.dataset.mentionName)
      }
    }

    this.render(orphans, unused)
  }

  // WHICH word the method uses for this ingredient, or null if none of them.
  //
  // The word rather than a yes/no, so a warning can say "Zucchini
  // (courgette)" — the list calls it one thing and the method calls it
  // another, and naming only the list's word leaves you to work out they are
  // the same vegetable.
  //
  // No terms means no opinion, and no opinion has to read as "named": that is
  // the answer which stays quiet in BOTH directions, since an unjudgeable row
  // should be reported neither as orphaned nor as unused.
  namedIn(row, text) {
    let terms
    try {
      terms = JSON.parse(row.dataset.mentionTerms || "[]")
    } catch {
      return NO_OPINION
    }
    if (!terms.length) return NO_OPINION

    return terms.find((term) => new RegExp(`\\b${escapeRegExp(term)}\\b`, "i").test(text)) || null
  }

  // "Zucchini (courgette)" when the two differ, "Paprika" when they do not.
  //
  // The alternative only shows when it is genuinely another WORD. A row's own
  // name in the other number is not — nobody needs "Berries (berry)" pointed
  // out — so the row also carries which terms are its own, and anything in
  // that set is treated as the same word.
  //
  // Deciding this by stripping a plural suffix was the first attempt and got
  // "Berries (berry)" wrong: berry/berries is not a suffix change. English
  // plurals are not a suffix rule, which is the same lesson the server side
  // learned.
  label(row, matched) {
    const name = row.dataset.mentionName
    if (!matched || matched === NO_OPINION) return name

    let own = []
    try {
      own = JSON.parse(row.dataset.mentionOwnTerms || "[]")
    } catch {
      return name
    }

    return own.includes(matched) ? name : `${name} (${matched})`
  }

  // Two ways a row goes: a saved one is hidden with its _destroy flag set, a
  // brand-new one is taken out of the document altogether. Only the first can
  // still be seen here, which is the only one that matters — a row you never
  // saved cannot be named in a method you have already written.
  beingRemoved(row) {
    return row.querySelector("[data-ingredients-target='destroy']")?.value === "1"
  }

  // Trix keeps the document in a hidden input beside the editor; reading the
  // editor's own textContent works too but includes the toolbar's labels.
  methodText() {
    const editor = document.querySelector("trix-editor")
    if (editor?.editor) return editor.editor.getDocument().toString()

    return document.querySelector("input[name*='[method]']")?.value || ""
  }

  render(orphans, unused) {
    if (this.hasNoticeTarget) {
      this.noticeTarget.hidden = orphans.length === 0
      if (orphans.length) this.listTarget.textContent = orphans.join(", ")
    }

    if (this.hasUnusedTarget) {
      this.unusedTarget.hidden = unused.length === 0
      if (unused.length) this.unusedListTarget.textContent = unused.join(", ")
    }
  }
}

// Distinct from a real match, so a row we cannot judge reads as "named"
// (staying quiet both ways) without label() then trying to print it.
const NO_OPINION = true

function escapeRegExp(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}
